/* -------------------------------------------------------------------------------------
 * Helper Functions
 * -------------------------------------------------------------------------------------
 */
export const injectDynamicFees = (copyContent: string, feeMap: any, convertToUpToPricing?: boolean) => {
  if (!isCopyDynamic(copyContent)) {
    return;
  }
  if (feeMap) {
    let newContent = copyContent;

    if (newContent.includes("up to $[rsn_fee]")) {
      newContent = newContent.replace("up to $[rsn_fee]", "$[rsn_fee]");
    }

    // if a calculation error, replace content with "up to $[tag]"
    if (convertToUpToPricing === true) {
      const convert = (text: string, tag: string) => {
        if (text?.includes(`$${tag}`)) {
          if (text?.includes(`up to $${tag}`)) {
            return text?.replace(tag, feeMap[tag]);
          }
          return text?.replace(`$${tag}`, `up to $${feeMap[tag]}`)
        }
        return text;
      }
      newContent = convert(newContent, "[rsn_fee]");
      newContent = convert(newContent, "[total_price]");
      newContent = convert(newContent, "[ext_total_price]");
      return newContent;
    }
    newContent = newContent?.replace?.(
      /\[total_price\]|\[rsn_fee\]|\[gemini_amount\]|\[offer_duration\]|\[offer_discount\]|\[ext_total_price]|\[ext_offer_duration\]|\[ext_offer_discount\]/g,
      matched => feeMap[matched] || matched
    ) || "";
    return newContent;
  }
};

export const isCopyDynamic = (copyText: string) => {
  if (!copyText) {
    return false;
  }
  return ['[total_price]', '[rsn_fee]', '[gemini_amount]', '[offer_duration]', '[offer_discount]', '[ext_total_price]', '[ext_offer_duration]', '[ext_offer_discount]']
    .some(tag => copyText?.includes(tag));
};

export const hasMultipleOffers = (copyObj: any[]) => {
  if (!copyObj || !Array.isArray(copyObj)) {
    return false;
  }

  return ['[offer_duration]', '[offer_discount]', '[ext_total_price]', '[ext_offer_duration]', '[ext_offer_discount]']
    .some(tag => copyObj?.some(copy => copy?.content?.includes(tag)));
};

export const isValidPrice = (price: any): boolean => {
  if (isNaN(price) || price === null || price === undefined) {
    return false;
  }
  if (typeof price === "number") {
    return isFinite(price) && price >= 0 && price >= 0.0 && price >= 0.00;
  }
  if (typeof price === "string") {
    // any number of digits period "."
    // optionally a period and exactly two digits after
    const validPrice = /^\d+(\.\d{2})?$/;
    return price !== "" && validPrice.test(price);
  }
  return false;
}

export const packageNameNormalizing = (data: string) => {
  if (!data) {
    return '';
  }
  const normalizedString = data?.normalize("NFD")?.replace(/[\u0300-\u036f]/g, "");
  const cleanedString = normalizedString?.replace(/[^a-zA-Z0-9\s]/g, "");
  return cleanedString ? cleanedString : '';
};

// This module is in the CLIENT bundle graph: `utils/index.ts` re-exports it
// (`export *`) into 4 plan-card components, and package.json declares no
// `sideEffects`, so a bundler can and will pull it into the client chunk — and it is
// EAGERLY loaded on most pages. Do NOT import `dynamic-injection/grammar` or
// `dynamic-injection/vocab` here — those are server-only in intent, and importing
// them from this module would drag them into that EAGER chunk. (They are already
// reachable from a separate, LAZY, live-preview-only chunk via
// `useStoryblokLivePage`'s dynamic `page-adapter` import — see
// channels-interactive-tool-adapter.v2.ts:28-31 — this note is about keeping them
// out of the eager plan-card graph, not about client-reachability in absolute
// terms.) The server-only predicates that need them (e.g. `isCopyFetchDependent`)
// live in `lib/helper/dynamic-injection/zip-dependency.ts`.
//
// `getOrdinal` / `getMultiOfferDuration` now live in
// lib/helper/dynamic-injection/offer-ladder as the SINGLE source of truth. They were
// duplicated here byte-for-byte on the stated grounds of keeping `lib/` free of a
// `src/` dependency — but that constraint is one-directional, and this module's own
// neighbours (useLiveAllIn, plan-cards/utils/classes) already import FROM lib. The
// wording is legal copy that must read identically whether it came from the token
// service or the legacy adapter, so it cannot be allowed to drift.
// Re-exported so existing importers (ai-product-recommender) are unaffected.
// (WAT-11203.)
// RELATIVE, not the bare `lib/*` tsconfig path: this module is reachable from SERVER
// code (plan-card-adapter -> page-adapter -> cache populate), and the server builds
// (tsconfig.server.json / tsconfig.dev.json) run under plain node with NO runtime path
// mapping, so a bare specifier emits an unresolvable require and crashes boot.
export { getMultiOfferDuration } from "../../../../../lib/helper/dynamic-injection/offer-ladder";
/** ------------------------------------------------------------------------------------ */
/**
 * @description Add dynamic fees ([rsn_fee], [total_price]) to copy
 * @params cardData (cardSet), offerPricesData, rsnFeeNum, isCardSetSatellite, isNoLocals, convertToUpToPricing
 * @returns modifiedOfferPrices, modifiedPriceCopyArrs
 */
export function getCardsWithDynamicData({
  cards,
  rsnFeeNum,
  useGeminiFee,
  convertToUpToPricing
}) {
  try {
    const totalPrices = [];
    const moddedCards = [];

    for (const card of cards) {
      const priceCopyObj = card?.productPrice?.priceCopy || [];
      const injectionIsNeeded = priceCopyObj?.some(copy => isCopyDynamic(copy?.content));

      let totalPrice: number;
      let extTotalPrice: number;
      const subtotalPrice = card?.pricingData?.offerSubtotal; // offer + (opt. gemini fee)
      const extSubtotalPrice = card?.pricingData?.extOfferSubtotal; // offer + (opt. gemini fee) + discount
      const useRsnFee = card?.pricingData?.useRsnFee ?? true;
      const rsnFee = card?.pricingData?.rsnFee || rsnFeeNum;

      /* - if no copy, or no copy content has [/] indicators, push content and continue
         * - push moddedContent as original content to simplify loading logic
        */
      if (priceCopyObj?.length === 0 || !injectionIsNeeded) {
        totalPrices.push(subtotalPrice.toFixed(2));
        moddedCards.push({
          ...card,
          productPrice: {
            ...card?.productPrice,
            priceCopy: card?.productPrice?.priceCopy?.map(copy => ({
              ...copy,
              moddedContent: copy?.content
            })),
            totalPriceNum: subtotalPrice.toFixed(2)
          }
        });
        continue;
      }

      /* - Calculate dynamic total prices, inject them, and check for errors */
      if (isValidPrice(subtotalPrice) && isValidPrice(rsnFeeNum)) {
        totalPrice = (subtotalPrice + (useRsnFee ? rsnFeeNum : 0)).toFixed(2);
        totalPrices.push(totalPrice);
      }
      if (!isValidPrice(totalPrice)) {
        throw new Error(`total price calculation failed :( subtotalPrice: ${subtotalPrice} rsnFee: '${rsnFeeNum} totalPrice: ${totalPrice}'`);
      }

      if (hasMultipleOffers(priceCopyObj)) {
        if (isValidPrice(extSubtotalPrice) && isValidPrice(rsnFeeNum)) {
          extTotalPrice = (extSubtotalPrice + rsnFee).toFixed(2);
        }
        if (!isValidPrice(extTotalPrice)) {
          throw new Error(`extended price calculation failed :( subtotalPrice: ${extSubtotalPrice} rsnFee: '${rsnFeeNum} totalPrice: ${extTotalPrice}'`);
        }
      }

      const injectMap: any = {
        ...card?.pricingData?.ssrCopyMap,
        '[total_price]': totalPrice,
        '[ext_total_price]': extTotalPrice,
        '[rsn_fee]': rsnFeeNum.toFixed(2)
      };

      if (priceCopyObj?.length > 0) {
        const moddedCopy = [];
        for (const copyObj of priceCopyObj) {
          moddedCopy.push({
            ...copyObj,
            moddedContent: injectDynamicFees(copyObj?.content, injectMap, convertToUpToPricing)
          });
        }
        moddedCards.push({
          ...card,
          productPrice: {
            ...card?.productPrice,
            priceCopy: moddedCopy,
            totalPriceNum: totalPrice // analytics reference
          }
        });
      }
    }

    return {
      moddedCards,
      totalPrices
    };
  } catch (err) {
    console.error("[PlanCard DynamicPrices] Error in dynamic price calculation", err);
    try {
      if (!convertToUpToPricing) {
        return getCardsWithDynamicData({
          cards,
          rsnFeeNum,
          useGeminiFee,
          convertToUpToPricing: true
        });
      }
      return {
        moddedCards: cards.map(card => ({
          ...card,
          productPrice: {
            ...card?.productPrice,
            priceCopy: []
          }
        })),
        totalPrices: cards.map(() => null)
      };
    } catch (err) {
      console.error("[PlanCard DynamicPrices] FATAL: Error setting fallback prices", err);
      return {
        moddedCards: cards.map(card => ({
          ...card,
          productPrice: {
            ...card?.productPrice,
            priceCopy: []
          }
        })),
        totalPrices: cards.map(() => null)
      };
    }
  }
};
